import { useMutation } from "@apollo/client";
import { TierIiReportStep } from "generated-graphql/graphql";
import { useCallback, useEffect, useState } from "react";
import { useNavigateToNextStep } from "./useNavigateToNextStep";
import { GET_REPORT } from "./useReport";
import { gql } from "generated-graphql";

const TOUCH_TIER_II_REPORT_STEP = gql(`
  mutation TouchTierIIReportStep($reportId: ID!, $step: TierIIReportStep!) {
    touchTierIIReportStep(reportId: $reportId, step: $step)
  }
`);

export function useTouchReportMutation(
  reportId: string,
  touchedSteps: TierIiReportStep[],
  step: TierIiReportStep,
  shouldNavigate: boolean | undefined = true
) {
  const navigateToNextStep = useNavigateToNextStep({ currentStep: step });
  const [pendingNavigation, setPendingNavigation] = useState(false);

  const [touchTierIIReportStep, { loading }] = useMutation(
    TOUCH_TIER_II_REPORT_STEP,
    {
      refetchQueries: [GET_REPORT],
      update: (cache) => {
        cache.modify({
          id: cache.identify({
            __typename: "TierIIReport",
            id: reportId,
          }),
          fields: {
            touchedSteps: () => Array.from(new Set([...touchedSteps, step])),
          },
          optimistic: true,
          broadcast: true,
        });
      },
    }
  );

  // Effect to handle navigation after state and cache updates
  useEffect(() => {
    if (pendingNavigation) {
      setPendingNavigation(false);
      navigateToNextStep(true, step);
    }
  }, [pendingNavigation, navigateToNextStep, step]);

  const handleSave = useCallback(async () => {
    try {
      if (!touchedSteps.includes(step)) {
        await touchTierIIReportStep({
          variables: {
            reportId,
            step,
          },
        });
      }
      if (shouldNavigate) {
        // Trigger a state update that will cause re-render and then navigate
        setPendingNavigation(true);
      }
    } catch (error) {
      // Log the error but don't rethrow - touching a step should not fail the save
      console.error("Error touching report step:", error);
      throw error;
    }
  }, [
    touchedSteps,
    step,
    shouldNavigate,
    touchTierIIReportStep,
    reportId,
  ]);

  return { handleSave, loading };
}
